// Multi-select category tree for the PLP filter drawer (:plp_multi_select_categories):
// a refinementList over the flat `category_paths` facet, ORing selections at any
// depth. Category/subcategory rows are expand-only; labels stay real SEO links
// for crawlers, with clicks intercepted as checkbox toggles.
import { connectRefinementList } from 'instantsearch.js/es/connectors'
import {
  buildCategoryTree,
  hasCheckbox,
  hierarchyToPath,
  selectionAfterToggle
} from '../../utils/search/categoryPaths'
import { getLocaleFromPath, prependLocale } from '../../utils/localeHelpers'
import { t } from '../../utils/search/translations'
import chevronRightIcon from '../../images/icons/chevron-right-grey-rounded.svg'
import chevronDownIcon from '../../images/icons/chevron-down-grey-rounded.svg'

function expandButton (node, expanded) {
  if (node.children.length === 0) return ''

  return `
    <button type="button" class="vjs-category-expand flex items-center p-4" aria-expanded="${expanded}" aria-label="${expanded ? t('collapse_category') : t('expand_category')} ${node.label}">
      <img src="${expanded ? chevronDownIcon : chevronRightIcon}" alt="">
    </button>
  `
}

function countSpan (node) {
  if (node.count === null) return ''
  return `<span class="vjs-category-count text-size-sm text-midnight-black-60">${node.count.toLocaleString()}</span>`
}

function categoryHref (path) {
  return prependLocale('/' + hierarchyToPath(path), getLocaleFromPath(window.location.pathname))
}

function labelLink (node) {
  return `<a class="vjs-category-label grow min-w-0 no-underline text-size-sm text-midnight-black ${node.isRefined ? 'font-semibold' : ''}" href="${categoryHref(node.path)}">${node.label}</a>`
}

function checkboxRow (node, expanded) {
  const indeterminate = !node.isRefined && node.children.some(child => child.refinedInBranch)

  return `
    <div class="vjs-category-row flex items-center gap-8 h-32 cursor-pointer">
      <input type="checkbox" class="ais-RefinementList-checkbox" tabindex="-1" aria-label="${node.label}"
        ${node.isRefined ? 'checked' : ''} ${indeterminate ? 'data-indeterminate="true"' : ''}>
      ${labelLink(node)}
      ${countSpan(node)}
      ${expandButton(node, expanded)}
    </div>
  `
}

function scopeRow (node, expanded) {
  return `
    <div class="vjs-category-row flex items-center gap-8 h-32 ${node.children.length > 0 ? 'cursor-pointer' : ''}">
      ${expandButton(node, expanded)}
      ${labelLink(node)}
      ${countSpan(node)}
    </div>
  `
}

function isExpanded (runtime, node) {
  if (runtime.expansionOverrides.has(node.path)) return runtime.expansionOverrides.get(node.path)

  const childRefined = node.children.some(child => child.refinedInBranch)
  return childRefined || (node.isRefined && !hasCheckbox(node.path))
}

function nodeHtml (runtime, node) {
  const expanded = isExpanded(runtime, node)
  const row = hasCheckbox(node.path) ? checkboxRow(node, expanded) : scopeRow(node, expanded)
  const children = expanded && node.children.length > 0
    ? `<ol class="pl-16">${node.children.map(child => nodeHtml(runtime, child)).join('')}</ol>`
    : ''

  return `
    <li class="vjs-category-node" data-path="${node.path}" data-checkbox="${hasCheckbox(node.path)}" data-expanded="${expanded}" data-has-children="${node.children.length > 0}">
      ${row}
      ${children}
    </li>
  `
}

// Every search response re-renders every widget; skip paints that would
// rebuild identical DOM (unless the container was emptied externally)
function paintKey (runtime) {
  const itemsKey = runtime.items.map(item => `${item.value}|${item.count}|${item.isRefined}`).join(',')
  const expansionKey = [...runtime.expansionOverrides].join(',')
  return `${itemsKey}#${expansionKey}`
}

function paintTree (runtime) {
  const key = paintKey(runtime)
  if (key === runtime.paintedKey && runtime.container.firstChild) return
  runtime.paintedKey = key

  const roots = buildCategoryTree(runtime.items)
  runtime.container.innerHTML = `<ol class="vjs-category-tree vsd-category-tree">${roots.map(root => nodeHtml(runtime, root)).join('')}</ol>`
  runtime.container.querySelectorAll('input[data-indeterminate="true"]').forEach(input => {
    input.indeterminate = true
  })
}

function toggleExpansion (runtime, listItem) {
  runtime.expansionOverrides.set(listItem.dataset.path, listItem.dataset.expanded !== 'true')
  paintTree(runtime)
}

function toggleSelection (runtime, path) {
  const selected = runtime.items.filter(item => item.isRefined).map(item => item.value)
  const next = selectionAfterToggle(selected, path)
  // ticking a node reveals its children; unticking never collapses
  if (next.includes(path)) runtime.expansionOverrides.set(path, true)
  const changed = selected.filter(value => !next.includes(value))
    .concat(next.filter(value => !selected.includes(value)))
  changed.forEach(value => runtime.refine(value))
}

function handleTreeClick (runtime, event) {
  const button = event.target.closest('.vjs-category-expand')
  if (button) {
    event.preventDefault()
    toggleExpansion(runtime, button.closest('.vjs-category-node'))
    return
  }

  const row = event.target.closest('.vjs-category-row')
  if (!row) return

  // Checkbox rows toggle their tick; scope rows with children toggle themselves
  // as the browse scope (results filter to the branch, one scope at a time,
  // replaced by any tick beneath — the navigation-free version of what clicking
  // a category did in the single-select tree). The chevron alone expands.
  const listItem = row.closest('.vjs-category-node')
  if (listItem.dataset.checkbox === 'true' || listItem.dataset.hasChildren === 'true') {
    event.preventDefault()
    toggleSelection(runtime, listItem.dataset.path)
  }
  // childless scope rows keep their real link navigation
}

// A URL-seeded refinement can differ from the facet value by casing. Swap one way
// only (count 0 -> twin with listings): the old value lingers at count 0 afterwards.
export function normaliseRefinedCasing (items, helper, attribute) {
  const swaps = items.filter(item => item.isRefined && item.count === 0).flatMap(item => {
    const twin = items.find(candidate => {
      return !candidate.isRefined && candidate.count > 0 && candidate.value.toLowerCase() === item.value.toLowerCase()
    })
    return twin && helper.state.isDisjunctiveFacetRefined(attribute, item.value) ? [[item.value, twin.value]] : []
  })
  if (swaps.length === 0) return

  const state = swaps.reduce((current, [value, twinValue]) => {
    return current.removeDisjunctiveFacetRefinement(attribute, value).addDisjunctiveFacetRefinement(attribute, twinValue)
  }, helper.state)
  helper.setState(state).search()
}

function renderCategoryTree (runtime, renderOptions, isFirstRender) {
  const { items, refine } = renderOptions
  runtime.items = items
  runtime.refine = refine

  if (isFirstRender) {
    runtime.container = document.querySelector(renderOptions.widgetParams.container)
    runtime.container.addEventListener('click', event => handleTreeClick(runtime, event))
  }

  // SSR hydration (:plp_initial_results) renders from the server blob and sends
  // no query — and until the category_paths backfill covers every record, that
  // blob can carry no category facet data, leaving the tree permanently empty.
  // One refresh recovers it: pages with hydrated facet data never pay it, and
  // once the backfill completes it never fires at all.
  if (!isFirstRender && items.length === 0 && renderOptions.instantSearchInstance.status === 'idle' && !runtime.refreshedEmptyTree) {
    runtime.refreshedEmptyTree = true
    renderOptions.instantSearchInstance.refresh()
    return
  }

  normaliseRefinedCasing(items, renderOptions.instantSearchInstance.mainIndex.getHelper(), renderOptions.widgetParams.attribute)
  paintTree(runtime)
}

export function categoryTreeFilter (widgetParams) {
  const runtime = { items: [], refine: null, container: null, expansionOverrides: new Map(), paintedKey: null, refreshedEmptyTree: false }

  const makeWidget = connectRefinementList((renderOptions, isFirstRender) => {
    return renderCategoryTree(runtime, renderOptions, isFirstRender)
  })

  return makeWidget({
    attribute: 'category_paths',
    operator: 'or',
    // the whole taxonomy is ~150 paths, so one facet response carries the full tree
    limit: 200,
    ...widgetParams
  })
}
